import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FiCreditCard, FiPlus } from "react-icons/fi";
import { loadStripeFor } from "../../../lib/stripeClient";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Money, formatMoneyCents } from "../../ui/Money";
import { cardLabel, cardSubLabel } from "../../ui/CardLabel";
import { ApiAuth, ApiError } from "../../../lib/api";
import { billingApi } from "./api";
import { mustCheckFirst } from "./paymentAttempt";
import {
  useCollect,
  useInvalidateBillingItems,
  usePaymentMethods,
} from "./hooks";
import { BillingItem, CollectRequestTarget, PaymentMethod } from "./types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  items: BillingItem[];
  selectedIds: Set<string>;
  auth: ApiAuth;
  stripePublicKey: string | null;
  // The clinic's connected account the key is for; null on its own keys.
  stripeAccount?: string | null;
  locationId: string | null;
  // Whose charges these are, when that is not the person signed in — a
  // guardian paying for a dependent. The CARD is always the payer's own, from
  // their own Stripe customer, which is why the saved-card list below needs no
  // scoping: there is nowhere in a dependent's record for another guardian's
  // payment method to appear.
  dependentPatientId?: string | null;
  // Named on the confirmation so a guardian who acts for two children is never
  // told only that "the payment went through".
  subjectName?: string | null;
};

function buildTargets(items: BillingItem[], selectedIds: Set<string>): {
  targets: CollectRequestTarget[];
  total: number;
} {
  const targets: CollectRequestTarget[] = [];
  let total = 0;
  for (const item of items) {
    if (!selectedIds.has(item.itemId)) continue;
    for (const t of item.payTargets) {
      if (t.outstandingCents <= 0) continue;
      targets.push({
        itemId: item.itemId,
        receivablePlanId: t.receivablePlanId,
        sourceRowId: t.sourceRowId,
        expectedOutstandingCents: t.outstandingCents,
        payAmountCents: t.outstandingCents,
      });
      total += t.outstandingCents;
    }
  }
  return { targets, total };
}

const NEW_CARD_VALUE = "__new_card__";

/**
 * What the confirmation says, in one place.
 *
 * "Your balance has been updated" is false when a guardian pays for somebody
 * else — their own balance did not move. Three separate toasts said it, which
 * is three places to forget; they now all read from here.
 */
function balanceLine(subjectName: string | null | undefined, pending: boolean) {
  const whose = subjectName ? `${subjectName}'s balance` : "Your balance";
  return pending ? `We'll update ${whose.toLowerCase()} once it clears.` : `${whose} has been updated.`;
}

/**
 * Confirms a new-card payment via the embedded PaymentElement. This branch
 * runs when the user picked "Use a new card" — the collect endpoint already
 * created the PaymentIntent, we just hand the client_secret to Stripe.
 */
function NewCardConfirm({
  auth,
  batchId,
  total,
  subjectName,
  onSuccess,
  onClose,
}: {
  auth: ApiAuth;
  batchId: string;
  total: number;
  subjectName?: string | null;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const toast = useToast();

  const onConfirm = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (result.error) {
        setErrorMsg(result.error.message || "Payment failed");
        setSubmitting(false);
        return;
      }
      const pi = result.paymentIntent;
      if (pi?.status === "succeeded" || pi?.status === "processing") {
        // Tell the backend to settle without waiting for the Stripe
        // webhook — critical on localhost where Stripe cannot reach us.
        try {
          await billingApi.syncBatch(auth, batchId);
        } catch (syncErr) {
          // Logged but not surfaced: the webhook is the safety net and
          // `settleCollectionIntent` is idempotent.
          // eslint-disable-next-line no-console
          console.warn("[patient-app] post-confirm sync failed", syncErr);
        }
        toast({
          title: "Payment received",
          description: balanceLine(subjectName, pi.status === "processing"),
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onSuccess();
      } else {
        setErrorMsg(`Unexpected status: ${pi?.status || "unknown"}`);
        setSubmitting(false);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || "Payment failed");
      setSubmitting(false);
    }
  };

  return (
    <>
      <PaymentElement />
      {errorMsg && (
        <Text fontSize="13px" color="#b42318" mt={3}>
          {errorMsg}
        </Text>
      )}
      <HStack mt={4} justify="flex-end" spacing={2}>
        <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
          Cancel
        </Button>
        <Button
          bg="#1a1f36"
          color="white"
          _hover={{ bg: "#2d3348" }}
          onClick={onConfirm}
          isLoading={submitting}
          loadingText="Processing"
        >
          Pay {formatMoneyCents(total)}
        </Button>
      </HStack>
    </>
  );
}

/**
 * Handles 3DS / SCA challenges for saved-card payments. The PaymentIntent
 * was created+confirmed server-side; if Stripe returned `requires_action`,
 * we let Stripe SDK pop up the authentication and then confirm again.
 */
function SavedCardChallenge({
  auth,
  batchId,
  clientSecret,
  onResolved,
}: {
  auth: ApiAuth;
  batchId: string;
  clientSecret: string;
  onResolved: (
    outcome: { ok: true } | { ok: false; errorMsg: string }
  ) => void;
}) {
  const stripe = useStripe();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!stripe || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const result = await stripe.handleNextAction({ clientSecret });
        if (result.error) {
          onResolved({
            ok: false,
            errorMsg: result.error.message || "Authentication failed",
          });
          return;
        }
        const pi = result.paymentIntent;
        if (pi?.status === "succeeded" || pi?.status === "processing") {
          // Mirror the post-confirm sync from the new-card path: the
          // webhook is the safety net but localhost-friendly tests need
          // an explicit settle ping.
          try {
            await billingApi.syncBatch(auth, batchId);
          } catch (syncErr) {
            // eslint-disable-next-line no-console
            console.warn("[patient-app] post-challenge sync failed", syncErr);
          }
          onResolved({ ok: true });
        } else {
          onResolved({
            ok: false,
            errorMsg: `Unexpected status: ${pi?.status || "unknown"}`,
          });
        }
      } catch (err: any) {
        onResolved({ ok: false, errorMsg: err?.message || "Authentication failed" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  return (
    <HStack spacing={2} color="#6b7280" fontSize="13px">
      <Spinner size="sm" />
      <Text>Authenticating with your bank…</Text>
    </HStack>
  );
}

export const PayDrawer = ({
  isOpen,
  onClose,
  items,
  selectedIds,
  auth,
  stripePublicKey,
  stripeAccount = null,
  locationId,
  dependentPatientId = null,
  subjectName = null,
}: Props) => {
  const { targets, total } = useMemo(
    () => buildTargets(items, selectedIds),
    [items, selectedIds]
  );
  const methods = usePaymentMethods(auth, isOpen);
  const collect = useCollect(auth, dependentPatientId);
  const invalidate = useInvalidateBillingItems();
  const toast = useToast();

  const savedCards: PaymentMethod[] = methods.data?.methods ?? [];
  const defaultCardId = methods.data?.defaultPaymentMethodId || null;

  const [selectedCardId, setSelectedCardId] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [phase, setPhase] = useState<
    "idle" | "creating" | "challenge" | "ready" | "succeeded" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [staleHint, setStaleHint] = useState<string | null>(null);

  // Reset between opens.
  useEffect(() => {
    if (!isOpen) {
      setSelectedCardId("");
      setClientSecret(null);
      setBatchId(null);
      setPhase("idle");
      setErrorMsg(null);
      setStaleHint(null);
      return;
    }
  }, [isOpen]);

  // Pre-select the default saved card (or first available) when methods load.
  useEffect(() => {
    if (!isOpen) return;
    if (selectedCardId) return;
    if (methods.isLoading) return;
    if (savedCards.length === 0) {
      setSelectedCardId(NEW_CARD_VALUE);
      return;
    }
    const def = savedCards.find((c) => c.id === defaultCardId);
    setSelectedCardId(def?.id || savedCards[0].id);
  }, [
    isOpen,
    selectedCardId,
    methods.isLoading,
    savedCards,
    defaultCardId,
  ]);

  const usingNewCard = selectedCardId === NEW_CARD_VALUE;

  // Kick off intent creation depending on the selected method:
  //   - Saved card: create + confirm immediately on the server. Handle 3DS
  //     here if Stripe returns requires_action.
  //   - New card: create the intent (no payment_method), embed Element,
  //     and let the user submit it.
  const startCollect = () => {
    if (targets.length === 0) return;
    if (!selectedCardId) return;
    setErrorMsg(null);
    setStaleHint(null);
    setPhase("creating");
    collect.mutate(
      {
        targets,
        locationId,
        paymentMethodId: usingNewCard ? null : selectedCardId,
        returnUrl: window.location.href,
      },
      {
        onSuccess: (data) => {
          setClientSecret(data.clientSecret);
          setBatchId(data.paymentBatchId);
          if (usingNewCard) {
            // Element will render and the user submits.
            setPhase("ready");
            return;
          }
          // Saved-card branch: BE already confirmed.
          if (data.status === "succeeded" || data.status === "processing") {
            setPhase("succeeded");
            toast({
              title: "Payment received",
              description: balanceLine(subjectName, data.status === "processing"),
              status: "success",
              duration: 5000,
              isClosable: true,
            });
            invalidate();
            onClose();
            return;
          }
          if (
            data.status === "requires_action" ||
            data.status === "requires_source_action"
          ) {
            setPhase("challenge");
            return;
          }
          if (data.status === "requires_payment_method") {
            setErrorMsg(
              "Your card was declined. Please pick another method or add a new card."
            );
            setPhase("error");
            return;
          }
          setErrorMsg(
            "The payment did not finish. Nothing was charged — please try again."
          );
          setPhase("error");
        },
        onError: (err) => {
          const apiErr = err as ApiError;
          if (apiErr?.code === "OUTSTANDING_CHANGED") {
            setStaleHint(
              "Some items just changed. Refreshing your balance — please review and try again."
            );
            invalidate();
            toast({
              title: "Balance changed",
              description: "We refreshed your items.",
              status: "warning",
              duration: 5000,
              isClosable: true,
            });
            onClose();
            return;
          }
          // The server could not confirm whether this went through: say so in
          // its words and read the balance again, so the person sees whether
          // it was paid before paying again. Never retried on its own.
          if (mustCheckFirst(apiErr)) {
            invalidate();
            setErrorMsg(apiErr.message);
            setPhase("error");
            return;
          }
          // A refusal the server wrote for the patient is said as it is; our
          // own failures (5xx, a network error) promise nothing they cannot
          // know (audit C33). A retry of the same payment carries the same
          // request id, so it is never charged twice.
          const status = Number(apiErr?.status || 0);
          setErrorMsg(
            status >= 400 && status < 500 && apiErr?.message
              ? apiErr.message
              : "We couldn't complete the payment request. Please try again — you won't be charged twice."
          );
          setPhase("error");
        },
      }
    );
  };

  const stripePromise = clientSecret ? loadStripeFor({ publishableKey: stripePublicKey, stripeAccount }) : null;

  const renderBody = () => {
    if (!stripePublicKey) {
      return (
        <Text fontSize="13px" color="#b42318">
          Payments are not configured for this clinic. Please contact the
          front desk.
        </Text>
      );
    }
    if (staleHint) {
      return (
        <Text fontSize="13px" color="#b54708">
          {staleHint}
        </Text>
      );
    }

    return (
      <Stack spacing={4}>
        <Box borderWidth="1px" borderColor="#e6e6e6" borderRadius="8px" p={3}>
          <Text
            fontSize="12px"
            color="#6b7280"
            mb={2}
            textTransform="uppercase"
            letterSpacing="0.04em"
          >
            You're paying
          </Text>
          <Stack spacing={1.5}>
            {Array.from(selectedIds).map((id) => {
              const item = items.find((i) => i.itemId === id);
              if (!item) return null;
              return (
                <HStack key={id} justify="space-between" fontSize="14px">
                  <Text color="#1a1f36" noOfLines={1}>
                    {item.title}
                  </Text>
                  <Money cents={item.amounts.payableNowCents} fontWeight="600" />
                </HStack>
              );
            })}
          </Stack>
          <HStack
            justify="space-between"
            mt={3}
            pt={3}
            borderTopWidth="1px"
            borderColor="#f0f0f0"
          >
            <Text fontSize="13px" color="#6b7280">
              Total
            </Text>
            <Money cents={total} fontSize="16px" fontWeight="700" color="#1a1f36" />
          </HStack>
        </Box>

        <Box>
          <Text
            fontSize="12px"
            color="#6b7280"
            mb={2}
            textTransform="uppercase"
            letterSpacing="0.04em"
          >
            Pay with
          </Text>
          {methods.isLoading ? (
            <HStack color="#6b7280" fontSize="13px">
              <Spinner size="xs" />
              <Text>Loading your saved cards…</Text>
            </HStack>
          ) : (
            <RadioGroup
              value={selectedCardId}
              onChange={(val) => {
                // Switching method invalidates the in-flight intent;
                // requires creating a new one on submit.
                setSelectedCardId(val);
                setClientSecret(null);
                setBatchId(null);
                setPhase("idle");
                setErrorMsg(null);
              }}
            >
              <Stack spacing={2}>
                {savedCards.map((pm) => (
                  <Flex
                    key={pm.id}
                    align="center"
                    gap={3}
                    px={3}
                    py={2.5}
                    borderWidth="1px"
                    borderColor={
                      selectedCardId === pm.id ? "#1a1f36" : "#e6e6e6"
                    }
                    borderRadius="8px"
                    cursor="pointer"
                    onClick={() => {
                      if (selectedCardId !== pm.id) {
                        setSelectedCardId(pm.id);
                        setClientSecret(null);
                                setPhase("idle");
                        setErrorMsg(null);
                      }
                    }}
                    _hover={{ bg: "#fafafa" }}
                  >
                    <Radio value={pm.id} isChecked={selectedCardId === pm.id} />
                    <FiCreditCard color="#6b7280" />
                    <Box flex={1}>
                      <Text fontSize="14px" color="#1a1f36" fontWeight="500">
                        {cardLabel(pm)}
                        {pm.isDefault && (
                          <Text
                            as="span"
                            ml={2}
                            fontSize="11px"
                            color="#067647"
                            fontWeight="500"
                          >
                            (default)
                          </Text>
                        )}
                      </Text>
                      {/* Carries the card itself once the patient's own name
                          replaces it above, so two Visas stay distinguishable
                          at the moment of paying. */}
                      <Text fontSize="12px" color="#6b7280">
                        {cardSubLabel(pm) || "-"}
                      </Text>
                    </Box>
                  </Flex>
                ))}
                <Flex
                  align="center"
                  gap={3}
                  px={3}
                  py={2.5}
                  borderWidth="1px"
                  borderColor={
                    selectedCardId === NEW_CARD_VALUE ? "#1a1f36" : "#e6e6e6"
                  }
                  borderRadius="8px"
                  borderStyle="dashed"
                  cursor="pointer"
                  onClick={() => {
                    if (selectedCardId !== NEW_CARD_VALUE) {
                      setSelectedCardId(NEW_CARD_VALUE);
                      setClientSecret(null);
                            setPhase("idle");
                      setErrorMsg(null);
                    }
                  }}
                  _hover={{ bg: "#fafafa" }}
                >
                  <Radio
                    value={NEW_CARD_VALUE}
                    isChecked={selectedCardId === NEW_CARD_VALUE}
                  />
                  <FiPlus color="#6b7280" />
                  <Text fontSize="14px" color="#1a1f36" fontWeight="500">
                    Use a new card
                  </Text>
                </Flex>
              </Stack>
            </RadioGroup>
          )}
        </Box>

        {errorMsg && (
          <Text fontSize="13px" color="#b42318">
            {errorMsg}
          </Text>
        )}

        {/* Saved-card flow: one button, server handles everything. */}
        {!usingNewCard && phase !== "challenge" && (
          <HStack justify="flex-end" spacing={2}>
            <Button variant="ghost" onClick={onClose} isDisabled={phase === "creating"}>
              Cancel
            </Button>
            <Button
              bg="#1a1f36"
              color="white"
              _hover={{ bg: "#2d3348" }}
              onClick={startCollect}
              isLoading={phase === "creating"}
              loadingText="Processing"
              isDisabled={!selectedCardId || phase === "creating"}
            >
              Pay {formatMoneyCents(total)}
            </Button>
          </HStack>
        )}

        {/* Saved-card 3DS challenge: Stripe SDK pops up the authentication. */}
        {!usingNewCard && phase === "challenge" && clientSecret && batchId && stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret, locale: "en" }}
          >
            <SavedCardChallenge
              auth={auth}
              batchId={batchId}
              clientSecret={clientSecret}
              onResolved={(outcome) => {
                if (outcome.ok) {
                  toast({
                    title: "Payment received",
                    description: balanceLine(subjectName, false),
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                  });
                  invalidate();
                  onClose();
                } else {
                  setErrorMsg(outcome.errorMsg);
                  setPhase("error");
                }
              }}
            />
          </Elements>
        )}

        {/* New-card flow: create intent on first click, then render Element. */}
        {usingNewCard && phase === "idle" && (
          <HStack justify="flex-end" spacing={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="#1a1f36"
              color="white"
              _hover={{ bg: "#2d3348" }}
              onClick={startCollect}
            >
              Continue
            </Button>
          </HStack>
        )}
        {usingNewCard && phase === "creating" && (
          <HStack color="#6b7280" fontSize="13px">
            <Spinner size="xs" />
            <Text>Preparing secure payment form…</Text>
          </HStack>
        )}
        {usingNewCard && phase === "ready" && clientSecret && batchId && stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: "stripe" },
              locale: "en",
            }}
          >
            <NewCardConfirm
              auth={auth}
              batchId={batchId}
              total={total}
              subjectName={subjectName}
              onSuccess={() => {
                invalidate();
                onClose();
              }}
              onClose={onClose}
            />
          </Elements>
        )}
      </Stack>
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="lg" fontWeight="600" color="#1a1f36">
          Pay
        </DrawerHeader>
        <DrawerBody>{renderBody()}</DrawerBody>
        <DrawerFooter borderTopWidth="1px" borderColor="#f0f0f0">
          <Text fontSize="12px" color="#6b7280">
            Secured by Stripe. We never store your card details.
          </Text>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
